'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef, useMemo, KeyboardEvent } from 'react';
import { useTheme } from 'next-themes';
import { FiSun, FiMoon, FiSearch, FiMenu, FiX, FiFileText, FiTerminal, FiClock } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { usePathname, useRouter } from 'next/navigation';

/* -------------------- SUB-COMPONENT: HIGHLIGHTER -------------------- */
const HighlightedText = ({ text, highlight, isSelected }: { text: string; highlight: string; isSelected: boolean }) => {
  if (!highlight.trim()) return <span>{text}</span>;
  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) => 
        regex.test(part) ? (
          <span key={i} className={isSelected ? "text-white underline underline-offset-2" : "text-indigo-600 dark:text-indigo-400 font-black"}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

/* -------------------- FOCUS TRAP HOOK -------------------- */
function useFocusTrap<T extends HTMLElement>(containerRef: React.RefObject<T | null>, isActive: boolean) {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;
    const focusableSelectors = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', '[tabindex]:not([tabindex="-1"])'];
    const elements = Array.from(containerRef.current.querySelectorAll<HTMLElement>(focusableSelectors.join(',')));
    if (!elements.length) return;
    const first = elements[0], last = elements[elements.length - 1];
    const handleKeyDown = (e: any) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
      else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
    };
    const currentRef = containerRef.current;
    currentRef.addEventListener('keydown', handleKeyDown);
    return () => currentRef?.removeEventListener('keydown', handleKeyDown);
  }, [containerRef, isActive]);
}

interface SearchItem { title: string; slug: string; type: 'page' | 'project' | 'blog'; }
interface NavbarProps { SidebarAnnouncement?: React.ReactNode; }

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Projects', href: '/projects' },
  { label: 'Blog', href: '/blog' },
  { label: 'Resume', href: '/resume' },
  { label: 'Contact', href: '/contact' },
  { label: 'Testimonials', href: '/testimonials' },
];

const groupOrder: SearchItem['type'][] = ['page', 'project', 'blog'];
const resolveHref = (item: SearchItem) => item.type === 'page' ? `/${item.slug}` : `/${item.type}/${item.slug}`;

const Navbar = ({ SidebarAnnouncement }: NavbarProps) => {
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  
  const allItems = useRef<SearchItem[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileBtnRef = useRef<HTMLButtonElement>(null);
  const lastScrollY = useRef(0);

  const [isOpen, setIsOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hideNavbar, setHideNavbar] = useState(false);
  const [recentSearches, setRecentSearches] = useState<SearchItem[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetch('/api/searchData').then(res => res.json()).then(data => allItems.current = data).catch(console.error);
    const saved = localStorage.getItem('recent_searches');
    if (saved) setRecentSearches(JSON.parse(saved));
  }, []);

  const addToRecent = (item: SearchItem) => {
    const updated = [item, ...recentSearches.filter(r => r.slug !== item.slug)].slice(0, 3);
    setRecentSearches(updated);
    localStorage.setItem('recent_searches', JSON.stringify(updated));
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); inputRef.current?.focus(); }
      if (e.key === 'Escape') { setSearchTerm(''); setActiveIndex(null); inputRef.current?.blur(); }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setScrollProgress((scrollTop / (scrollHeight - clientHeight)) * 100);
      if (!isOpen) setHideNavbar(scrollTop > lastScrollY.current && scrollTop > 50);
      lastScrollY.current = scrollTop;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isOpen]);

  const groupedResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const term = searchTerm.toLowerCase();
    const scored = allItems.current.map(item => {
      const title = item.title.toLowerCase();
      if (title.startsWith(term)) return { ...item, score: 2 };
      if (title.includes(term)) return { ...item, score: 1 };
      return null;
    }).filter(Boolean) as (SearchItem & { score: number })[];
    scored.sort((a, b) => b.score - a.score);
    return groupOrder.reduce((acc, type) => {
      const items = scored.filter(i => i.type === type);
      if (items.length) acc[type] = items;
      return acc;
    }, {} as Record<SearchItem['type'], SearchItem[]>);
  }, [searchTerm]);

  const flatResults = useMemo(() => groupedResults ? Object.values(groupedResults).flat() : [], [groupedResults]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const list = searchTerm ? flatResults : recentSearches;
    if (!list.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i === null || i === list.length - 1 ? 0 : i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => (i === null || i === 0 ? list.length - 1 : i - 1)); }
    if (e.key === 'Enter' && activeIndex !== null) { 
      const item = list[activeIndex];
      addToRecent(item);
      router.push(resolveHref(item)); 
      setSearchTerm(''); 
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) { setActiveIndex(null); setIsSearchFocused(false); }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node) && !mobileBtnRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [recentSearches]);

  useFocusTrap(mobileMenuRef, isOpen);

  return (
    <motion.nav className="sticky top-0 w-full z-40 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800" animate={{ y: hideNavbar ? -100 : 0 }} transition={{ duration: 0.3 }}>
      <motion.div className="hidden md:block absolute top-0 left-0 h-[3px] bg-indigo-600 z-[60]" animate={{ width: `${scrollProgress}%` }} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link href="/" className="flex items-center gap-2 flex-shrink-0">
          <Image src="/logo.png" alt="Eric Agyei" width={32} height={32} className="rounded-lg shadow-sm" />
          <span className="font-bold text-lg tracking-tight">Eric Agyei</span>
        </Link>
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <Link key={l.href} href={l.href} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all hover:bg-zinc-100 dark:hover:bg-zinc-800 ${pathname === l.href ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-500/10' : 'text-zinc-600 dark:text-zinc-400'}`}>{l.label}</Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative w-48 lg:w-64" ref={searchRef}>
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              ref={inputRef}
              value={searchTerm}
              onFocus={() => setIsSearchFocused(true)}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              className="pl-9 pr-4 py-1.5 text-xs w-full rounded-xl border border-zinc-200 dark:border-zinc-800 dark:bg-zinc-900/50 outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all font-mono uppercase tracking-widest"
            />
            <AnimatePresence>
              {(searchTerm || (isSearchFocused && recentSearches.length > 0)) && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 w-full mt-2 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden z-50 p-2">
                  <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                    {!searchTerm && recentSearches.length > 0 && (
                      <div className="mb-2">
                        <div className="px-3 py-2 text-[9px] font-black uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
                          <FiClock size={10}/> Recent_History
                        </div>
                        {recentSearches.map((item, idx) => (
                          <Link key={item.slug} href={resolveHref(item)} onClick={() => setIsSearchFocused(false)} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${activeIndex === idx ? 'bg-zinc-100 dark:bg-zinc-900' : ''}`}>
                            <span className="text-[9px] font-mono text-zinc-500">{item.type.slice(0,3).toUpperCase()}</span>
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">{item.title}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchTerm && flatResults.length > 0 ? (
                      Object.entries(groupedResults || {}).map(([type, items]) => (
                        <div key={type} className="mb-4 last:mb-0">
                          <div className="px-3 py-2 text-[9px] font-black uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2"><span className="w-1 h-1 bg-indigo-500 rounded-full" /> {type}s</div>
                          {items.map((item) => {
                            const isSelected = flatResults[activeIndex!]?.slug === item.slug;
                            return (
                              <Link key={item.slug} href={resolveHref(item)} onClick={() => { addToRecent(item); setSearchTerm(''); }} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${isSelected ? 'bg-indigo-600 text-white translate-x-1' : 'hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-600 dark:text-zinc-400'}`}>
                                <span className={`text-[9px] font-mono font-bold ${isSelected ? 'text-indigo-200' : 'text-zinc-500'}`}>{item.type === 'blog' ? 'LOG' : item.type === 'project' ? 'OBJ' : 'SRC'}</span>
                                <span className="text-xs font-bold truncate tracking-tight">
                                  <HighlightedText text={item.title} highlight={searchTerm} isSelected={isSelected} />
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      ))
                    ) : searchTerm && (
                      <div className="py-12 text-center">
                        <FiTerminal className="mx-auto mb-3 text-red-500 animate-pulse" size={20} />
                        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Signal_Not_Found</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 mt-2 border-t border-zinc-100 dark:border-zinc-900 flex justify-between items-center text-[8px] font-mono text-zinc-500 uppercase tracking-widest">
                    <span><kbd className="border rounded px-1 mr-1">↑↓</kbd> Nav</span>
                    <span className="text-indigo-500 animate-pulse font-bold">Encrypted_Link: Active</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {mounted && (
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="w-10 h-10 rounded-xl border border-zinc-200 dark:border-zinc-800 flex items-center justify-center hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all">
              {theme === 'dark' ? <FiSun className="text-amber-500" /> : <FiMoon className="text-indigo-400" />}
            </button>
          )}
        </div>
        <button ref={mobileBtnRef} onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2 rounded-xl bg-zinc-100 dark:bg-zinc-800 transition-transform active:scale-90">{isOpen ? <FiX size={20} /> : <FiMenu size={20} />}</button>
      </div>
      {/* Mobile Menu Overlay Code Remains Same as Your Original */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-6 py-8 flex flex-col gap-6 overflow-hidden"
            ref={mobileMenuRef}
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  onClick={() => setIsOpen(false)}
                  className={`text-2xl font-bold tracking-tight py-2 ${pathname === l.href ? 'text-indigo-600' : 'text-zinc-400'}`}
                >
                  {l.label}
                </Link>
              ))}
            </div>

            {SidebarAnnouncement && (
              <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                {SidebarAnnouncement}
              </div>
            )}

            {mounted && (
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800"
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Appearance</span>
                {theme === 'dark' ? <FiSun className="text-amber-500 text-xl" /> : <FiMoon className="text-indigo-400 text-xl" />}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;